// RichTextEditor.js
import React, { useRef, useEffect, useMemo, forwardRef, useState } from 'react';
import Quill from 'quill';

import 'quill/dist/quill.snow.css';
import { Button } from 'antd';
import { getUserDetails, makePostCall, PatientHeader } from '../../utils/helper';
import CustomEditor from '../custom-editor';
import TinyEditor from './tinymce-editor';

const RichTextEditor = forwardRef(({ content, onChange, onSave, handleSaveShortCut, cancel, currentReport, patDetails, fromReporting, orderDetails }, ref) => {

  const {pacs_order, assigned_radiologist_name} = patDetails;
  const {patient} = pacs_order;

  const userDetails = getUserDetails();
  const userType = userDetails?.user_type;

  const [footerTemplate, setFooterTemplate] = useState('');
  const [showFooter, setShowFooter] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await makePostCall('/no-auth/report-header-config', { action: 'get' });
        if (response?.data?.config) {
          const config = response.data.config;

          // Check if footer should be shown (default to true if not specified)
          const shouldShowFooter = config.showFooter !== false;
          setShowFooter(shouldShowFooter);

          // Only set footer template for NEW reports
          // Existing reports already have footer in content
          if (shouldShowFooter && !(currentReport && currentReport.pr_report_footer)) {
            setFooterTemplate(config.footerTemplate || 'Note: This is a professional opinion only, Each investigation has its limitations. Final diagnosis needs correlation with clinical context and other investigations. Kindly discuss, if necessary.');
          } else if (currentReport && currentReport.pr_report_footer) {
            setFooterTemplate(''); // Empty, footer already in content
          } else {
            setFooterTemplate(''); // Empty footer if disabled
          }
        } else {
          // No config found, use defaults only for NEW reports
          setShowFooter(true);
          if (!(currentReport && currentReport.pr_report_footer)) {
            setFooterTemplate('Note: This is a professional opinion only, Each investigation has its limitations. Final diagnosis needs correlation with clinical context and other investigations. Kindly discuss, if necessary.');
          } else {
            setFooterTemplate('');
          }
        }
      } catch (error) {
        console.log('Error loading footer config:', error);
        // Use default footer if config fetch fails, only for NEW reports
        setShowFooter(true);
        if (!(currentReport && currentReport.pr_report_footer)) {
          setFooterTemplate('Note: This is a professional opinion only, Each investigation has its limitations. Final diagnosis needs correlation with clinical context and other investigations. Kindly discuss, if necessary.');
        } else {
          setFooterTemplate('');
        }
      } finally {
        setConfigLoaded(true);
      }
    };
    loadConfig();
  }, [currentReport]);

  const handleSave = (status) => {
    onSave && onSave(content, status, currentReport,);
  }

  const handlePrint = () => {
    makePostCall('/print-report', {
      report: currentReport,
      patDetails: patDetails,
      html: content, //.replaceAll(' ', '&nbsp'),
    }, {
      responseType: "arraybuffer",
    })
      .then(res => {
        const pdfBlob = new Blob([res.data], { type: "application/pdf" });
        const pdf_url = URL.createObjectURL(pdfBlob);
        // setPdfUrl(url);
        const printWindow = window.open(pdf_url, "_blank");
        printWindow.print();
      })
      .catch(err => {
        console.log("Error", err);
      })
  }

  const statusOrder = ['DRAFTED', 'SIGNEDOFF'];

  const headerContent = useMemo(() => {
    // Only generate header if current report doesn't have stored header
    // This is for NEW reports only, existing reports already have header in content
    if (currentReport && currentReport.pr_report_header) {
      return null; // Don't generate, already loaded in content
    }
    return PatientHeader(patient, currentReport, pacs_order, assigned_radiologist_name)
  }, [patDetails, currentReport, patient, pacs_order, assigned_radiologist_name]);

  // Strip footer from content if showFooter is disabled and content has stored footer
  const processedContent = useMemo(() => {
    if (!showFooter && content) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      const footerEl = tempDiv.querySelector('.page-footer-content');
      if (footerEl) footerEl.remove();
      return tempDiv.innerHTML;
    }
    return content;
  }, [content, showFooter]);

  // Don't render TinyEditor until config AND currentReport are loaded
  if (!configLoaded || !currentReport) {
    return (
      <div id='editor-container' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div>Loading editor...</div>
      </div>
    );
  }

  return (<div id='editor-container'>
    {/* <div ref={editorRef}></div> */}
    {/* <CustomEditor fromReporting={fromReporting} initialContent={content} placeholder={"placeholder..."} handleChange={onChange} /> */}
    <TinyEditor
      ref={ref}
      fromReporting={fromReporting}
      initialContent={processedContent}
      placeholder={"placeholder..."}
      handleChange={onChange}
      headerContent={headerContent}
      footerContent={footerTemplate}
      handleSaveShortCut={handleSaveShortCut}
    />
  </div>);
});

export default RichTextEditor;
